import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  Directive,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  AnalyticsService,
  CtaTrackingData,
  ScrollDepthPercent,
  ViewportStatus,
} from './google-analytics.service';

const VISIBILITY_THRESHOLD = 0.5;
const DWELL_TIME_MS = 1_000;
const SCROLL_DEPTH_BUCKETS: readonly ScrollDepthPercent[] = [90, 75, 50, 25, 0];

/**
 * Tracks an impression after at least 50% of the host element remains visible
 * for one continuous second. AnalyticsService performs the final once-per-
 * virtual-page-view deduplication.
 *
 * Supply identifiers explicitly through ctaTrackingData. The directive does
 * not infer identity from rendered text, CSS classes, or other DOM details.
 *
 * @example
 * ```html
 * <a
 *   ptzCtaTracking
 *   [ctaTrackingData]="insuranceCta"
 *   #cta="ptzCtaTracking"
 *   (click)="cta.emitClick()"
 *   routerLink="/insurance"
 * >
 *   Explore pet insurance
 * </a>
 * ```
 */
@Directive({
  selector: '[ptzCtaTracking]',
  standalone: true,
  exportAs: 'ptzCtaTracking',
})
export class CtaTrackingDirective implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) ctaTrackingData!: CtaTrackingData;

  private readonly browserWindow: Window | null;
  private observer: IntersectionObserver | null = null;
  private dwellTimeoutId: number | null = null;
  private hasEmittedImpression = false;
  private viewportStatus: ViewportStatus = 'below_fold';
  private initialized = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly analytics: AnalyticsService,
    private readonly destroyRef: DestroyRef,
    @Inject(DOCUMENT) document: Document,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.browserWindow = isPlatformBrowser(platformId)
      ? document.defaultView
      : null;
  }

  ngOnInit(): void {
    if (!this.browserWindow) {
      return;
    }

    this.initialized = true;
    this.viewportStatus = this.computeViewportStatus();
    this.setUpObserver();

    // Re-arm only after AnalyticsService actually emits a new virtual view.
    // This covers navigation and supported selected-pet changes without
    // introducing a second router lifecycle in the directive.
    this.analytics.ctaPageViewReset$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rearmForNewPageView());

    // Time spent in a hidden tab cannot satisfy continuous visibility.
    this.browserWindow.document.addEventListener(
      'visibilitychange',
      this.handleDocumentVisibilityChange,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const trackingChange = changes['ctaTrackingData'];

    if (
      !this.initialized ||
      !this.browserWindow ||
      !trackingChange ||
      trackingChange.firstChange
    ) {
      return;
    }

    const previous = trackingChange.previousValue as
      | CtaTrackingData
      | undefined;
    const current = trackingChange.currentValue as CtaTrackingData | undefined;

    // Re-arm when the tracked CTA identity changes on a reused host element.
    // Changes to copy or URL alone do not make this a new CTA impression.
    if (previous?.cta_id !== current?.cta_id) {
      this.rearmForCurrentCta();
    }
  }

  ngOnDestroy(): void {
    this.clearDwellTimer();
    this.observer?.disconnect();
    this.browserWindow?.document.removeEventListener(
      'visibilitychange',
      this.handleDocumentVisibilityChange,
    );
  }

  /** Call from the CTA's intentional click handler before its action. */
  emitClick(): void {
    this.analytics.trackCtaClick(this.ctaTrackingData);
  }

  private setUpObserver(): void {
    if (!this.browserWindow) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) =>
        this.handleIntersection(entries),
      { threshold: [0, VISIBILITY_THRESHOLD] },
    );

    this.observer.observe(this.elementRef.nativeElement);
  }

  private rearmForNewPageView(): void {
    if (!this.browserWindow) {
      return;
    }

    this.hasEmittedImpression = false;
    this.clearDwellTimer();
    this.viewportStatus = this.computeViewportStatus();
    this.refreshObservation();
  }

  private rearmForCurrentCta(): void {
    if (!this.browserWindow) {
      return;
    }

    this.hasEmittedImpression = false;
    this.clearDwellTimer();
    this.viewportStatus = this.computeViewportStatus();
    this.refreshObservation();
  }

  private refreshObservation(): void {
    this.observer?.unobserve(this.elementRef.nativeElement);
    this.observer?.observe(this.elementRef.nativeElement);
    this.checkCurrentVisibility();
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    if (this.hasEmittedImpression) {
      return;
    }

    // Array.at() depends on a newer lib target than some Angular 18
    // workspaces use, so use index access for compatibility.
    const entry = entries[entries.length - 1];

    if (!entry) {
      return;
    }

    const isVisibleEnough =
      entry.isIntersecting && entry.intersectionRatio >= VISIBILITY_THRESHOLD;

    if (isVisibleEnough && this.isDocumentVisible()) {
      this.startDwellTimer();
      return;
    }

    this.clearDwellTimer();
  }

  private readonly handleDocumentVisibilityChange = (): void => {
    if (!this.isDocumentVisible()) {
      this.clearDwellTimer();
      return;
    }

    this.checkCurrentVisibility();
  };

  private checkCurrentVisibility(): void {
    if (
      !this.browserWindow ||
      this.hasEmittedImpression ||
      !this.isDocumentVisible()
    ) {
      return;
    }

    if (this.getVisibleAreaRatio() >= VISIBILITY_THRESHOLD) {
      this.startDwellTimer();
      return;
    }

    this.clearDwellTimer();
  }

  private getVisibleAreaRatio(): number {
    if (!this.browserWindow) {
      return 0;
    }

    const rect = this.elementRef.nativeElement.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) {
      return 0;
    }

    const viewportWidth =
      this.browserWindow.innerWidth ||
      this.browserWindow.document.documentElement.clientWidth;
    const viewportHeight =
      this.browserWindow.innerHeight ||
      this.browserWindow.document.documentElement.clientHeight;

    const visibleWidth = Math.max(
      0,
      Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0),
    );
    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0),
    );

    return (visibleWidth * visibleHeight) / (rect.width * rect.height);
  }

  private startDwellTimer(): void {
    if (
      !this.browserWindow ||
      this.dwellTimeoutId !== null ||
      this.hasEmittedImpression
    ) {
      return;
    }

    this.dwellTimeoutId = this.browserWindow.setTimeout(() => {
      this.dwellTimeoutId = null;

      // Layout may change without another threshold callback, so confirm the
      // element is still eligible when the one-second period finishes.
      if (
        this.isDocumentVisible() &&
        this.getVisibleAreaRatio() >= VISIBILITY_THRESHOLD
      ) {
        this.emitImpression();
      }
    }, DWELL_TIME_MS);
  }

  private clearDwellTimer(): void {
    if (!this.browserWindow || this.dwellTimeoutId === null) {
      return;
    }

    this.browserWindow.clearTimeout(this.dwellTimeoutId);
    this.dwellTimeoutId = null;
  }

  private emitImpression(): void {
    if (this.hasEmittedImpression) {
      return;
    }

    // Mark locally before calling the service. The service provides the final
    // page-view-level deduplication shared by all directive instances.
    this.hasEmittedImpression = true;

    this.analytics.trackCtaImpression({
      ...this.ctaTrackingData,
      viewport_status: this.viewportStatus,
      scroll_depth_percent: this.computeScrollDepth(),
    });
  }

  /** Whether the CTA's document position lies within the initial fold. */
  private computeViewportStatus(): ViewportStatus {
    if (!this.browserWindow) {
      return 'below_fold';
    }

    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    const viewportHeight =
      this.browserWindow.innerHeight ||
      this.browserWindow.document.documentElement.clientHeight;
    const elementPageTop = rect.top + this.browserWindow.scrollY;

    return elementPageTop < viewportHeight ? 'above_fold' : 'below_fold';
  }

  private getScrollContainer(): HTMLElement {
    let parent = this.elementRef.nativeElement.parentElement;

    while (parent) {
      const styles = this.browserWindow?.getComputedStyle(parent);
      const overflowY = styles?.overflowY;

      const isScrollable =
        (overflowY === 'auto' ||
          overflowY === 'scroll' ||
          overflowY === 'overlay') &&
        parent.scrollHeight > parent.clientHeight;

      if (isScrollable) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return (
      (this.browserWindow?.document.scrollingElement as HTMLElement | null) ??
      this.browserWindow!.document.documentElement
    );
  }

  /** How far the user had scrolled when the CTA qualified as viewed. */
  private computeScrollDepth(): ScrollDepthPercent {
    if (!this.browserWindow) {
      return 0;
    }

    const scrollContainer = this.getScrollContainer();
    const scrollableHeight =
      scrollContainer.scrollHeight - scrollContainer.clientHeight;

    const percent =
      scrollableHeight > 0
        ? (scrollContainer.scrollTop / scrollableHeight) * 100
        : 0;

    return SCROLL_DEPTH_BUCKETS.find((bucket) => percent >= bucket) ?? 0;
  }

  private isDocumentVisible(): boolean {
    return this.browserWindow?.document.visibilityState !== 'hidden';
  }
}
